import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class HolidayNotificationService {
  // Notification state management
  private _notificationVisible = new BehaviorSubject<boolean>(false);

  // Public observables
  public notificationVisible$ = this._notificationVisible.asObservable();

  // Memorial Day 2024 - May 27th (last Monday of May)
  private readonly memorialDay2024 = new Date('2024-05-27');
  private readonly displayDays = 3; // Show for 3 days

  constructor() {
    this.checkHolidayVisibility();
    // Force show for testing period (May 23-30, 2025) - remove after Memorial Day
    this.testingForceShow();
  }

  /**
   * Checks if the holiday notification should be visible based on current date
   */
  private checkHolidayVisibility(): void {
    const today = new Date();
    const currentYear = today.getFullYear();

    // Calculate Memorial Day for current year (last Monday of May)
    const memorialDay = this.getMemorialDay(currentYear);

    // Check if we're within the display window (show 4 days before and 3 days after Memorial Day)
    const timeDiff = today.getTime() - memorialDay.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

    // Show if we're within -4 to +3 days from Memorial Day (8 day window total)
    const shouldShow = daysDiff >= -4 && daysDiff <= 3;

    // Only show if not already dismissed for this Memorial Day period
    const dismissedForThisPeriod = this.wasDismissedForMemorialDay(currentYear);

    this._notificationVisible.next(shouldShow && !dismissedForThisPeriod);
  }

  /**
   * Calculates Memorial Day for a given year (last Monday of May)
   */
  private getMemorialDay(year: number): Date {
    const may = new Date(year, 4, 31); // May 31st
    const dayOfWeek = may.getDay();
    const daysToSubtract = dayOfWeek === 1 ? 0 : (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
    return new Date(year, 4, 31 - daysToSubtract);
  }

  /**
   * Checks if the notification was dismissed for this Memorial Day period
   */
  private wasDismissedForMemorialDay(year: number): boolean {
    const dismissedYear = localStorage.getItem('holiday-notification-dismissed-year');
    if (!dismissedYear) return false;

    // Check if dismissed for current year's Memorial Day
    return parseInt(dismissedYear) === year;
  }

  /**
   * Hides the holiday notification and marks it as dismissed for this entire Memorial Day period
   */
  public hideNotification(): void {
    this._notificationVisible.next(false);
    const currentYear = new Date().getFullYear();
    localStorage.setItem('holiday-notification-dismissed-year', currentYear.toString());
  }

  /**
   * Gets the holiday message
   */
  public getHolidayMessage(): string {
    return "Thank you for using our system. Have a safe Memorial Day weekend.";
  }

  /**
   * Gets the holiday title
   */
  public getHolidayTitle(): string {
    return "Memorial Day Weekend";
  }

  /**
   * Force show notification (for testing)
   */
  public forceShowNotification(): void {
    localStorage.removeItem('holiday-notification-dismissed-year');
    this._notificationVisible.next(true);
  }

  /**
   * Temporary testing method to ensure visibility during Memorial Day week
   */
  private testingForceShow(): void {
    const today = new Date();
    const isMemorialDayWeek = today.getFullYear() === 2025 &&
                              today.getMonth() === 4 && // May (0-indexed)
                              today.getDate() >= 23 &&
                              today.getDate() <= 30;

    if (isMemorialDayWeek) {
      // Only force show if user hasn't dismissed it
      const dismissedForThisPeriod = this.wasDismissedForMemorialDay(today.getFullYear());

      if (!dismissedForThisPeriod) {
        LoggerService.log('Testing force show active for Memorial Day week 2025 - not previously dismissed');
        setTimeout(() => {
          this._notificationVisible.next(true);
        }, 500);
      } else {
        LoggerService.log('Testing force show skipped - user already dismissed Memorial Day notification');
      }
    }
  }
}
